import React, { useEffect, useRef, useState } from 'react';

import { Control, Controller, SubmitHandler, useForm } from 'react-hook-form';
import MUIProvider from '~/contexts/MUIProvider';
import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useDeleteDynamicPricingExclusion } from '~/hooks/apis/lere/useDeleteDynamicPricingExclusion';
import { useGetDynamicPricingExclusions } from '~/hooks/apis/lere/useGetDynamicPricingExclusions';
import { useUpsertDynamicPricingExclusion } from '~/hooks/apis/lere/useUpsertDynamicPricingExclusion';
import useCurrentUser from '~/hooks/useCurrentUser';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import { hasFullAccess, hasReadAccess } from '~/utils/lere';

import { useConfirmDialog } from '../Common/Providers/ConfirmDialogProvider';

import { DynamicPricingExclusionConfig, LereTabComponentProps } from './types';

type ExclusionField = 'auto_apply' | 'auto_yield' | 'early_surcharge' | 'appear_in_admin_portal';

type ExclusionFormInput = {
  entityId: string;
  autoApply: boolean;
  autoYield: boolean;
  earlySurcharge: boolean;
  appearInAdminPortal: boolean;
};

type ExclusionCheckboxField = keyof Omit<ExclusionFormInput, 'entityId'>;

type ExclusionRow = DynamicPricingExclusionConfig & {
  property_name?: string;
  vendor_id?: string;
};

type EntityMetadata = {
  propertyName?: string;
  vendorId?: string;
};

const defaultExclusionFlags: Omit<ExclusionFormInput, 'entityId'> = {
  autoApply: true,
  autoYield: true,
  earlySurcharge: true,
  appearInAdminPortal: true,
};
const LE_OFFER_ID_LENGTH = 18;

function ExclusionCheckbox({
  name,
  label,
  control,
  disabled,
}: {
  name: ExclusionCheckboxField;
  label: string;
  control: Control<ExclusionFormInput>;
  disabled: boolean;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Checkbox
              checked={field.value}
              disabled={disabled}
              onChange={(event) => field.onChange(event.target.checked)}
            />
          }
          label={label}
        />
      )}
    />
  );
}

function mapRowsByEntity(rows: Array<DynamicPricingExclusionConfig>): Record<string, DynamicPricingExclusionConfig> {
  return rows.reduce<Record<string, DynamicPricingExclusionConfig>>((acc, row) => {
    acc[row.entity_id] = row;
    return acc;
  }, {});
}

function hasUnsavedChanges(
  row: DynamicPricingExclusionConfig,
  persistedRowsByEntity: Record<string, DynamicPricingExclusionConfig>,
): boolean {
  const persisted = persistedRowsByEntity[row.entity_id];
  if (!persisted) {
    return false;
  }

  return (
    row.auto_apply !== persisted.auto_apply ||
    row.auto_yield !== persisted.auto_yield ||
    row.early_surcharge !== persisted.early_surcharge ||
    row.appear_in_admin_portal !== persisted.appear_in_admin_portal
  );
}

function buildExclusionPayload(row: DynamicPricingExclusionConfig): DynamicPricingExclusionConfig {
  return {
    entity_id: row.entity_id,
    auto_apply: row.auto_apply,
    auto_yield: row.auto_yield,
    early_surcharge: row.early_surcharge,
    appear_in_admin_portal: row.appear_in_admin_portal,
  };
}

function isOfferEntity(entityId: string): boolean {
  return entityId.length === LE_OFFER_ID_LENGTH;
}

function getEntityPath(entityId: string): string {
  return `/offers/${entityId}`;
}

function getPropertyPath(vendorId: string, propertyId: string): string {
  return `/vendors/${vendorId}/properties/${propertyId}`;
}

async function getEntityMetadataForEntity(entityId: string): Promise<EntityMetadata> {
  if (!isOfferEntity(entityId)) {
    const propertyResponse = await ReservationService.getProperty(entityId);
    return {
      propertyName: propertyResponse?.result?.name,
      vendorId: propertyResponse?.result?.id_salesforce_external,
    };
  }

  const offerResponse = await OffersService.getOffer(entityId);
  const propertyId = offerResponse?.result?.packages?.[0]?.fk_property_id;

  if (!propertyId) {
    return {};
  }

  const propertyResponse = await ReservationService.getProperty(propertyId);
  return {
    propertyName: propertyResponse?.result?.name,
  };
}

export default function RMExclusionOverrides({ onLereError }: LereTabComponentProps) {
  const showConfirmDialog = useConfirmDialog();
  const { user } = useCurrentUser();
  const canManageExclusions = hasFullAccess(user);
  const canReadExclusions = hasReadAccess(user) || canManageExclusions;
  const exclusionsQuery = useGetDynamicPricingExclusions({ enabled: canReadExclusions });
  const upsertExclusionMutation = useUpsertDynamicPricingExclusion();
  const deleteExclusionMutation = useDeleteDynamicPricingExclusion();
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<ExclusionFormInput>({
    defaultValues: {
      entityId: '',
      ...defaultExclusionFlags,
    },
  });

  const [rows, setRows] = useState<Array<ExclusionRow>>([]);
  const [persistedRowsByEntity, setPersistedRowsByEntity] = useState<Record<string, DynamicPricingExclusionConfig>>({});
  const entityMetadataByEntityRef = useRef<Map<string, EntityMetadata>>(new Map());

  useEffect(() => {
    if (!exclusionsQuery.data) {
      return;
    }

    let isCancelled = false;
    const sortedRows = [...exclusionsQuery.data].sort((a, b) => a.entity_id.localeCompare(b.entity_id));
    const uniqueEntityIds = [...new Set(sortedRows.map((row) => row.entity_id))];
    const missingEntityIds = uniqueEntityIds.filter((entityId) => !entityMetadataByEntityRef.current.has(entityId));

    setRows(
      sortedRows.map((row) => ({
        ...row,
        property_name: entityMetadataByEntityRef.current.get(row.entity_id)?.propertyName,
        vendor_id: entityMetadataByEntityRef.current.get(row.entity_id)?.vendorId,
      })),
    );
    setPersistedRowsByEntity(mapRowsByEntity(sortedRows));

    if (missingEntityIds.length === 0) {
      return () => {
        isCancelled = true;
      };
    }

    const hydrateMissingPropertyNames = async () => {
      const metadataPairs = await Promise.all(
        missingEntityIds.map(async (entityId) => {
          try {
            const metadata = await getEntityMetadataForEntity(entityId);
            return [entityId, metadata] as const;
          } catch {
            return [entityId, {}] as const;
          }
        }),
      );

      if (isCancelled) {
        return;
      }

      metadataPairs.forEach(([entityId, metadata]) => {
        entityMetadataByEntityRef.current.set(entityId, metadata);
      });

      setRows((previousRows) =>
        previousRows.map((row) => ({
          ...row,
          property_name: entityMetadataByEntityRef.current.get(row.entity_id)?.propertyName,
          vendor_id: entityMetadataByEntityRef.current.get(row.entity_id)?.vendorId,
        })),
      );
    };

    hydrateMissingPropertyNames().catch(onLereError);

    return () => {
      isCancelled = true;
    };
  }, [exclusionsQuery.data, onLereError]);

  useEffect(() => {
    if (exclusionsQuery.error) {
      onLereError(exclusionsQuery.error);
    }
  }, [exclusionsQuery.error, onLereError]);

  const updatingEntityId = upsertExclusionMutation.isPending ? upsertExclusionMutation.variables?.entity_id : null;
  const deletingEntityId = deleteExclusionMutation.isPending ? deleteExclusionMutation.variables : null;

  const setRowField = (entityId: string, field: ExclusionField, checked: boolean) => {
    if (!canManageExclusions) {
      return;
    }

    setRows((previousRows) =>
      previousRows.map((row) => (row.entity_id === entityId ? { ...row, [field]: checked } : row)),
    );
  };

  const submitExclusion: SubmitHandler<ExclusionFormInput> = async (data) => {
    if (!canManageExclusions) {
      return;
    }

    const trimmedEntityId = data.entityId.trim();
    if (!trimmedEntityId) {
      setError('entityId', { message: 'ID is required' });
      return;
    }

    try {
      await upsertExclusionMutation.mutateAsync({
        entity_id: trimmedEntityId,
        auto_apply: data.autoApply,
        auto_yield: data.autoYield,
        early_surcharge: data.earlySurcharge,
        appear_in_admin_portal: data.appearInAdminPortal,
      });

      reset({
        entityId: '',
        ...defaultExclusionFlags,
      });
    } catch (error) {
      onLereError(error);
    }
  };

  const handleUpdate = async (row: DynamicPricingExclusionConfig) => {
    if (!canManageExclusions) {
      return;
    }

    try {
      await upsertExclusionMutation.mutateAsync(buildExclusionPayload(row));
    } catch (error) {
      onLereError(error);
    }
  };

  const handleDelete = async (entityId: string) => {
    if (!canManageExclusions) {
      return;
    }

    const shouldDelete = await showConfirmDialog({
      title: 'Remove exclusion',
      description: `Remove ${entityId} from the dynamic pricing exclusion list?`,
    });

    if (!shouldDelete) {
      return;
    }

    try {
      await deleteExclusionMutation.mutateAsync(entityId);
    } catch (error) {
      onLereError(error);
    }
  };

  const columns: Array<GridColDef<ExclusionRow>> = [
    {
      field: 'entity_id',
      headerName: 'Offer or Property ID',
      flex: 2,
      minWidth: 280,
      renderCell: ({ row }) => {
        let path: string | null = null;

        if (isOfferEntity(row.entity_id)) {
          path = getEntityPath(row.entity_id);
        } else if (row.vendor_id) {
          path = getPropertyPath(row.vendor_id, row.entity_id);
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            {path ? (
              <Link component={RouterLink} to={path} target="_blank" title="id">
                {row.entity_id}
              </Link>
            ) : (
              <Typography variant="body2">{row.entity_id}</Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'property_name',
      headerName: 'Property Name',
      flex: 2,
      minWidth: 280,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            lineHeight: 1.4,
            py: 1,
          }}
        >
          {row.property_name || '-'}
        </Box>
      ),
    },
    {
      field: 'auto_apply',
      headerName: 'Auto Apply',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Checkbox
          checked={row.auto_apply}
          onChange={(event) => setRowField(row.entity_id, 'auto_apply', event.target.checked)}
          disabled={!canManageExclusions || row.entity_id === updatingEntityId || row.entity_id === deletingEntityId}
        />
      ),
    },
    {
      field: 'auto_yield',
      headerName: 'Auto Yield',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Checkbox
          checked={row.auto_yield}
          onChange={(event) => setRowField(row.entity_id, 'auto_yield', event.target.checked)}
          disabled={!canManageExclusions || row.entity_id === updatingEntityId || row.entity_id === deletingEntityId}
        />
      ),
    },
    {
      field: 'early_surcharge',
      headerName: 'Early Surcharge',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Checkbox
          checked={row.early_surcharge}
          onChange={(event) => setRowField(row.entity_id, 'early_surcharge', event.target.checked)}
          disabled={!canManageExclusions || row.entity_id === updatingEntityId || row.entity_id === deletingEntityId}
        />
      ),
    },
    {
      field: 'appear_in_admin_portal',
      headerName: 'Appear On Admin Portal',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Checkbox
          checked={row.appear_in_admin_portal}
          onChange={(event) => setRowField(row.entity_id, 'appear_in_admin_portal', event.target.checked)}
          disabled={!canManageExclusions || row.entity_id === updatingEntityId || row.entity_id === deletingEntityId}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleUpdate(row)}
            disabled={
              !canManageExclusions ||
              row.entity_id === updatingEntityId ||
              row.entity_id === deletingEntityId ||
              !hasUnsavedChanges(row, persistedRowsByEntity)
            }
            sx={{ mr: 1 }}
          >
            Update
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => handleDelete(row.entity_id)}
            disabled={!canManageExclusions || row.entity_id === updatingEntityId || row.entity_id === deletingEntityId}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <MUIProvider>
      <Box>
        <Typography variant="h2" fontSize="2em" fontWeight="medium">
          Exclude By Offer or Property
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          Add or update exclusions for dynamic pricing automation and admin portal visibility. By default, all types of
          surcharges are allowed.
        </Typography>

        {!canManageExclusions && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You have read-only access. LERE Admin permissions are required to create, update, or delete exclusions.
          </Alert>
        )}

        <Box component="form" sx={{ mt: 2 }} onSubmit={handleSubmit(submitExclusion)}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Offer or Property ID"
                disabled={!canManageExclusions || upsertExclusionMutation.isPending}
                error={Boolean(errors.entityId)}
                helperText={errors.entityId?.message}
                {...register('entityId', { required: 'ID is required' })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} alignItems="center" height="100%">
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!canManageExclusions || upsertExclusionMutation.isPending}
                >
                  Exclude
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}>
                <ExclusionCheckbox
                  name="autoApply"
                  label="Auto Apply"
                  control={control}
                  disabled={!canManageExclusions || upsertExclusionMutation.isPending}
                />
                <ExclusionCheckbox
                  name="autoYield"
                  label="Auto Yield"
                  control={control}
                  disabled={!canManageExclusions || upsertExclusionMutation.isPending}
                />
                <ExclusionCheckbox
                  name="earlySurcharge"
                  label="Early Surcharge"
                  control={control}
                  disabled={!canManageExclusions || upsertExclusionMutation.isPending}
                />
                <ExclusionCheckbox
                  name="appearInAdminPortal"
                  label="Appear On Admin Portal"
                  control={control}
                  disabled={!canManageExclusions || upsertExclusionMutation.isPending}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
            Current Exclusions
          </Typography>
          <DataGrid
            autoHeight
            columns={columns}
            rows={rows}
            loading={exclusionsQuery.isFetching}
            getRowHeight={() => 'auto'}
            getRowId={(row) => row.entity_id}
            disableRowSelectionOnClick
            disableColumnMenu
            pageSizeOptions={[10, 20, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
          />
        </Box>
      </Box>
    </MUIProvider>
  );
}
